/**
 * Voice Coordinator
 *
 * Global singleton to coordinate voice recording and playback.
 * Ensures only one voice activity happens at a time:
 * - Only one audio plays at a time
 * - Recording stops all audio playback
 * - Sending messages stops all audio playback
 */

type VoiceActivity = "playback" | "recording" | "idle";

class VoiceCoordinator {
  private currentActivity: VoiceActivity = "idle";
  private playbackCallbacks: Map<string, () => void> = new Map();
  private recordingCallback: (() => void) | null = null;
  private globalAudioInstance: HTMLAudioElement | null = null;
  private globalPlayingMessageId: string | null = null;

  /**
   * Register a playback instance
   * @param messageId - Unique message identifier
   * @param stopCallback - Function to call when this playback should stop
   */
  registerPlayback(messageId: string, stopCallback: () => void): void {
    this.playbackCallbacks.set(messageId, stopCallback);
  }

  /**
   * Unregister a playback instance
   * @param messageId - Unique message identifier
   */
  unregisterPlayback(messageId: string): void {
    this.playbackCallbacks.delete(messageId);
  }

  /**
   * Register a recording instance
   * @param stopCallback - Function to call when recording should stop
   */
  registerRecording(stopCallback: () => void): void {
    this.recordingCallback = stopCallback;
  }

  /**
   * Unregister recording instance
   */
  unregisterRecording(): void {
    this.recordingCallback = null;
  }

  /**
   * Start playback for a specific message
   * Stops all other playback and any recording
   * @param messageId - Unique message identifier
   * @param audioInstance - The audio element to play
   */
  startPlayback(messageId: string, audioInstance: HTMLAudioElement): void {
    // Stop all other playback
    this.stopAllPlayback();

    // Stop any recording
    this.stopAllRecording();

    // Set current activity
    this.currentActivity = "playback";
    this.globalAudioInstance = audioInstance;
    this.globalPlayingMessageId = messageId;
  }

  /**
   * Stop playback for a specific message
   * @param messageId - Unique message identifier
   */
  stopPlayback(messageId: string): void {
    if (this.globalPlayingMessageId === messageId) {
      this.stopGlobalAudio();
      this.currentActivity = "idle";
    }
  }

  /**
   * Stop all audio playback
   */
  stopAllPlayback(): void {
    // Stop global audio instance
    this.stopGlobalAudio();

    // Call all registered stop callbacks
    this.playbackCallbacks.forEach((stopCallback) => {
      try {
        stopCallback();
      } catch (error) {
        console.error("Error stopping playback:", error);
      }
    });

    if (this.currentActivity === "playback") {
      this.currentActivity = "idle";
    }
  }

  /**
   * Start recording
   * Stops all playback
   */
  startRecording(): void {
    // Stop all playback first
    this.stopAllPlayback();

    // Set current activity
    this.currentActivity = "recording";
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    if (this.currentActivity === "recording") {
      this.currentActivity = "idle";
    }
  }

  /**
   * Stop all recording
   */
  stopAllRecording(): void {
    if (this.recordingCallback) {
      try {
        this.recordingCallback();
      } catch (error) {
        console.error("Error stopping recording:", error);
      }
    }

    if (this.currentActivity === "recording") {
      this.currentActivity = "idle";
    }
  }

  /**
   * Stop all voice activities (playback and recording)
   */
  stopAll(): void {
    this.stopAllPlayback();
    this.stopAllRecording();
    this.currentActivity = "idle";
  }

  /**
   * Get current activity
   */
  getCurrentActivity(): VoiceActivity {
    return this.currentActivity;
  }

  /**
   * Get currently playing message ID
   */
  getPlayingMessageId(): string | null {
    return this.globalPlayingMessageId;
  }

  /**
   * Check if a specific message is playing
   */
  isMessagePlaying(messageId: string): boolean {
    return (
      this.globalPlayingMessageId === messageId &&
      this.currentActivity === "playback"
    );
  }

  /**
   * Check if recording is currently active
   */
  isRecording(): boolean {
    return this.currentActivity === "recording";
  }

  /**
   * Stop global audio instance
   */
  private stopGlobalAudio(): void {
    if (this.globalAudioInstance) {
      try {
        this.globalAudioInstance.pause();
        this.globalAudioInstance.currentTime = 0;
      } catch (error) {
        console.error("Error stopping global audio:", error);
      }
      this.globalAudioInstance = null;
    }
    this.globalPlayingMessageId = null;
  }

  /**
   * Reset all state (useful for cleanup)
   */
  reset(): void {
    this.stopAll();
    this.playbackCallbacks.clear();
    this.recordingCallback = null;
  }
}

// Export singleton instance
const voiceCoordinator = new VoiceCoordinator();

export { voiceCoordinator };
export type { VoiceActivity };

